import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import BudgetControlAllocationGrid from './BudgetControlAllocationGrid';
import BudgetControlHistoryGrid from './BudgetControlHistoryGrid';
import BudgetControlStatusChip from './BudgetControlStatusChip';
import BudgetControlSummary from './BudgetControlSummary';
import {
  activateBudgetControl,
  completeBudgetControl,
  createBudgetControl,
  getBudgetControlWorkbench,
  reviewBudgetControl,
  reviseBudgetControl,
  saveBudgetControlAllocations,
} from './budgetcontrol.apis';
import {
  BudgetControlAllocation,
  BudgetControlAllocationSaveItem,
  BudgetControlWorkbenchModel,
} from './budgetcontrol.models';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';

type Transition = 'REVIEW' | 'ACTIVATE' | 'REVISE' | 'COMPLETE';

type State = {
  loading: boolean;
  saving: boolean;
  model?: BudgetControlWorkbenchModel;
  rows: BudgetControlAllocation[];
  error?: string;
  success?: string;
  transition?: Transition;
  remarks: string;
  createOpen: boolean;
  capacity: string;
  currency: string;
  name: string;
};

export default class BudgetControlWorkbench extends React.Component<
  PlanningPageProps,
  State
> {
  state: State = {
    loading: false,
    saving: false,
    rows: [],
    remarks: '',
    createOpen: false,
    capacity: '',
    currency: 'XOF',
    name: '',
  };

  componentDidMount() {
    this.initialize();
  }
  componentDidUpdate(prev: PlanningPageProps) {
    if (
      Number(prev.plancontext?.pci || 0) !==
      Number(this.props.plancontext?.pci || 0)
    )
      this.initialize();
  }

  private planningCycleId = () => Number(this.props.plancontext?.pci || 0);

  initialize = async () => {
    const pci = this.planningCycleId();
    if (!pci) {
      this.setState({ error: 'Planning Cycle context is missing.' });
      return;
    }
    this.setState({ loading: true, error: undefined });
    try {
      const model = await getBudgetControlWorkbench(pci);
      this.setState({
        model,
        rows: model.allocations,
        capacity:
          model.capacity.amount == null ? '' : String(model.capacity.amount),
        currency: model.capacity.currencycode || 'XOF',
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  create = async () => {
    const cp = this.state.model?.capacity;
    const direct = cp?.sourcecode === 'DIRECT_CAPACITY';
    const cap = this.state.capacity === '' ? null : Number(this.state.capacity);
    if (direct && (!Number.isFinite(cap) || Number(cap) <= 0)) {
      this.setState({ error: 'Enter the approved planning capacity.' });
      return;
    }
    this.setState({ saving: true, error: undefined });
    try {
      await createBudgetControl({
        planningCycleId: this.planningCycleId(),
        capacityAmount: direct ? cap : null,
        currencyCode: this.state.currency,
        name: this.state.name,
      });
      this.setState({
        createOpen: false,
        success: 'Budget Control draft created.',
      });
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  saveAllocations = async (rows: BudgetControlAllocationSaveItem[]) => {
    const c = this.state.model?.control;
    if (!c) return;
    this.setState({ saving: true, error: undefined });
    try {
      await saveBudgetControlAllocations(c.budgetcontrolid, c.stamp, rows);
      this.setState({ success: 'Budget distribution saved.' });
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  execute = async () => {
    const m = this.state.model,
      c = m?.control,
      t = this.state.transition;
    if (!t) return;
    this.setState({ saving: true, error: undefined });
    try {
      if (t === 'REVIEW' && c)
        await reviewBudgetControl(
          c.budgetcontrolid,
          c.stamp,
          this.state.remarks
        );
      if (t === 'ACTIVATE' && c)
        await activateBudgetControl(
          c.budgetcontrolid,
          c.stamp,
          this.state.remarks
        );
      if (t === 'REVISE' && m?.governance.active)
        await reviseBudgetControl(
          m.governance.active.budgetcontrolid,
          null,
          this.state.remarks
        );
      if (t === 'COMPLETE') {
        const r = await completeBudgetControl(
          this.planningCycleId(),
          this.state.remarks
        );
        const next = r?.sd?.actions?.nextpage ?? r?.actions?.nextpage;
        if (next) this.props.setPage(next);
      }
      this.setState({
        transition: undefined,
        remarks: '',
        success: `${t} completed.`,
      });
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  actions = () => {
    const m = this.state.model,
      c = m?.control,
      a = m?.actions;
    if (!a) return null;
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {a.canCreate && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.setState({ createOpen: true })}
          >
            Create Control
          </Button>
        )}
        {a.canReview && c && (
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => this.setState({ transition: 'REVIEW' })}
          >
            Mark Reviewed
          </Button>
        )}
        {a.canActivate && c && (
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayCircleOutlineIcon />}
            onClick={() => this.setState({ transition: 'ACTIVATE' })}
          >
            Activate
          </Button>
        )}
        {a.canCreateRevision && m.governance.active && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => this.setState({ transition: 'REVISE' })}
          >
            Create Revision
          </Button>
        )}
        {/* {a.canComplete && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => this.setState({ transition: 'COMPLETE' })}
          >
            Complete Budget Control
          </Button>
        )} */}
      </Stack>
    );
  };

  render() {
    const m = this.state.model,
      c = m?.control,
      cp = m?.capacity;
    const direct = cp?.sourcecode === 'DIRECT_CAPACITY';
    return (
      <PlanningWorkbenchShell
        {...this.props}

        workspacePhaseCode={"BUDGET_CONTROL"}
        runtimeContext={m?.context}

        onBack={this.props.onBack}
        title="Budget Control"
        subtitle="Govern how approved capacity is distributed across operational budgets."
        icon={<AccountBalanceIcon />}
        headerExtension={
          c ? (
            <BudgetControlStatusChip
              stateCode={c.statecode}
              current={c.iscurrent === 1}
            />
          ) : undefined
        }
        summary={
          c && m ? (
            <BudgetControlSummary
              model={c}
              capacity={m.capacity}
              governance={m.governance}
            />
          ) : undefined
        }
        actions={this.actions()}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.initialize}
      >
        {!c && m && !m.actions.canCreate && (
          <Alert severity="info">
            No Budget Control is available for this Planning Cycle.
          </Alert>
        )}
        {c && m && (
          <BudgetControlAllocationGrid
            rows={this.state.rows}
            capacityAmount={c.capacityamount}
            currencyCode={c.currencycode}
            editable={m.actions.canEditDistribution}
            saving={this.state.saving}
            onRowsChange={(rows) => this.setState({ rows })}
            onSave={this.saveAllocations}
          />
        )}
        {m && m.history.length > 0 && (
          <BudgetControlHistoryGrid rows={m.history} />
        )}
        <Dialog
          open={this.state.createOpen}
          onClose={() => this.setState({ createOpen: false })}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Create Budget Control</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {cp && (
                <Alert severity={direct ? 'info' : 'success'}>
                  <Typography variant="body2" fontWeight={700}>
                    {cp.sourcename}
                  </Typography>
                  {cp.determinantbudgetcode && (
                    <Typography variant="caption">
                      {cp.determinantbudgetcode}
                    </Typography>
                  )}
                </Alert>
              )}
              <TextField
                label="Control name"
                value={this.state.name}
                onChange={(e) => this.setState({ name: e.target.value })}
              />
              <TextField
                label="Approved capacity"
                type="number"
                value={this.state.capacity}
                disabled={!cp?.editable}
                helperText={
                  cp?.editable
                    ? 'Enter the approved direct planning capacity.'
                    : 'Capacity is governed by the authoritative upstream source.'
                }
                onChange={(e) => this.setState({ capacity: e.target.value })}
              />
              <TextField
                label="Currency"
                value={this.state.currency}
                disabled={!cp?.editable}
                onChange={(e) =>
                  this.setState({ currency: e.target.value.toUpperCase() })
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ createOpen: false })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={this.create}
              disabled={this.state.saving}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!this.state.transition}
          onClose={() => this.setState({ transition: undefined })}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{this.state.transition}</DialogTitle>
          <DialogContent>
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              multiline
              minRows={3}
              label="Remarks"
              value={this.state.remarks}
              onChange={(e) => this.setState({ remarks: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ transition: undefined })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={this.execute}
              disabled={this.state.saving}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </PlanningWorkbenchShell>
    );
  }
}
